import { inject } from '@angular/core';
import {
  Router,
  type ActivatedRouteSnapshot,
  type ResolveFn,
} from '@angular/router';
import { Store } from '@ngxs/store';
import { firstValueFrom } from 'rxjs';

import { LocalStorageService } from '@cosmos/data-access-storage';
import { collectRouteParams, collectRouteQueryParams } from '@cosmos/router';
import {
  CustomerPortalAuthActions,
  isPresentationUuid,
} from '@customer-portal/auth/data-access-auth';
import { handleLoginErrorRedirect } from '@customer-portal/auth/data-access-customer-portal-access-guard';
import { PreviewService } from '@customer-portal/data-access-preview';
import {
  CustomerPortalDistributorQueries,
  CustomerPortalPresentationQueries,
  injectPresentationQuoteDraftRestoreHelper,
} from '@customer-portal/presentations/data-access-presentations';
import {
  CustomerPortalDistributorActions,
  CustomerPortalPresentationActions,
} from '@customer-portal/presentations/data-access-presentations/actions';
import { CustomerPortalProjectDetailsActions } from '@customer-portal/projects/data-access/project-details/actions';
import { CustomerPortalProjectDetailsQueries } from '@customer-portal/projects/data-access/project-details/queries';
import {
  CustomerPortalProjectActions,
  CustomerPortalProjectQueries,
} from '@customer-portal/projects/data-access/store';
import { injectProductRequestRestoreHelper } from '@website-modules/product-requests/data-access-product-requests';

import { injectTemplateFontHelper } from '../utils/presentation.util';

/**
 * Resolver for the `/presentations/:presentationId` pages.
 *
 * 1. Loads presentation based on `presentationId` route param; if presentation is already in the store, it doesn't reload it.
 * 2. Loads presentation's distributor and design if not already loaded; this is needed for the presentation to be displayed in the UI.
 * 3. If presentation is associated with a project, loads the project as well if it's not already loaded.
 */
export const resolvePresentation: ResolveFn<void> = async (
  route: ActivatedRouteSnapshot
) => {
  const store = inject(Store);
  const router = inject(Router);
  const previewService = inject(PreviewService);
  const localStorageService = inject(LocalStorageService);
  const { presentationId: presentationIdParam } = collectRouteParams(
    route.root
  );
  const productRequestRestoreHelper = injectProductRequestRestoreHelper({
    store,
    localStorageService,
  });
  const quoteDraftRestoreHelper = injectPresentationQuoteDraftRestoreHelper({
    store,
    localStorageService,
  });
  // A standalone presentation is shared as `/presentations/{uuid}` with no
  // access code; a project one keeps its numeric id. Both arrive as strings,
  // so only the shape of the value tells them apart — and a uuid must not be
  // parsed, or every call below would be made with `NaN`.
  const isUuid = isPresentationUuid(presentationIdParam);
  const presentationKey: string | number = isUuid
    ? presentationIdParam
    : parseInt(presentationIdParam, 10);
  const { settings: settingsParam } = collectRouteQueryParams(route.root);
  const templateFontHelper = injectTemplateFontHelper();

  const presentationSignal = store.selectSignal(
    CustomerPortalPresentationQueries.getPresentation
  );

  /** Whether the presentation in the store is the one the URL asks for. */
  const matchesRoute = () => {
    const presentation = presentationSignal();
    return isUuid
      ? presentation?.Uuid === presentationIdParam
      : presentation?.Id === presentationKey;
  };

  // The distributor and the design are addressed by the presentation's numeric
  // id, which a uuid route does not know until the presentation itself is
  // loaded. Fire-and-forget either way, just at different moments.
  const loadDistributorAndDesign = (presentationId: number) => {
    if (
      !store.selectSnapshot(CustomerPortalDistributorQueries.getDistributor)
    ) {
      store.dispatch([
        new CustomerPortalDistributorActions.GetDistributor(presentationId),
      ]);
    }

    if (!store.selectSnapshot(CustomerPortalDistributorQueries.getDesign)) {
      store.dispatch([
        new CustomerPortalDistributorActions.GetDesign(presentationId),
      ]);
    }
  };

  // In mock/preview mode, skip loading if we already have the correct presentation
  if (previewService.useMockProjectAndPresentation && matchesRoute()) {
    return;
  }

  if (!isUuid) {
    loadDistributorAndDesign(presentationKey as number);
  }

  // Check if presentation is already loaded
  // if not, fire loading and wait since we need `ProjectId` to decide about project loading and other UI behavior
  if (!matchesRoute()) {
    try {
      await firstValueFrom(
        store.dispatch(
          new CustomerPortalPresentationActions.GetPresentation({
            presentationId: presentationKey,
            shouldLoadQuote: true,
            shouldLoadTemplate: true,
            settings: settingsParam ?? undefined,
          })
        ),
        { defaultValue: undefined }
      );
    } catch (error) {
      // A uuid route carries its own secret, so the session can be thrown away
      // and rebuilt from the link alone. Do that whenever the load fails: a
      // stored token the server no longer accepts for this presentation — one
      // minted before an access rule changed, say — keeps failing, and because
      // every guard short-circuits on the session it finds, nothing ever logs
      // in again. The visitor would be stuck until they cleared site data by
      // hand. Dropping it here lets the next navigation log in clean.
      if (isUuid) {
        store.dispatch(
          new CustomerPortalAuthActions.ClearSession({
            presentationId: presentationIdParam,
          })
        );
      }
      // Letting this reject aborts the navigation, which leaves the visitor on
      // a blank page at the site root with the link they followed gone from the
      // address bar. Render the error page in place instead — the same
      // `skipLocationChange` redirect the auth guards use — so the url still
      // holds the presentation they were trying to open.
      await handleLoginErrorRedirect(error, router);
      return;
    }
  } else if (settingsParam) {
    store.dispatch(
      new CustomerPortalPresentationActions.ApplyPresentationSettingsParams(
        settingsParam
      )
    );
  }

  const presentation = presentationSignal();
  if (!presentation) {
    // The load reported success but left nothing behind, so there is no page to
    // draw. Say so rather than rendering an empty shell.
    await handleLoginErrorRedirect(null, router);
    return;
  }

  if (isUuid) {
    // Now that the presentation is loaded, its numeric id is known.
    loadDistributorAndDesign(presentation.Id);
  }

  // Preload template custom font if it's not the default one Inter.
  // Just fire and don't wait so we can start showing loader.
  templateFontHelper.preloadTemplateFont();

  // The basket is a quote draft now, and a share link carries no visitor
  // identity — so the draft id in local storage is the only thing that ties a
  // returning visitor back to their own basket. Restore it for every
  // presentation in quote-request mode, project-linked or not.
  // Fire-and-forget: the badge can appear a beat late.
  const requestMode = store.selectSnapshot(
    CustomerPortalPresentationQueries.getPresentationRequestMode
  );

  if (requestMode === 'quote-request') {
    quoteDraftRestoreHelper.tryToRestoreQuoteDraftSession(presentation.Id);
  } else {
    productRequestRestoreHelper.tryToRestoreProductRequestSession(
      presentation.Id.toString()
    );
  }

  if (!presentation.ProjectId) {
    // Standalone presentation — no project related data to load.
    return;
  }

  // At this point we are dealing with a project-based presentation.

  const project = store.selectSnapshot(CustomerPortalProjectQueries.getProject);
  // Check if we have the project loaded, if not, load it (fire-and-forget)
  if (project?.Id !== presentation.ProjectId) {
    store.dispatch([
      new CustomerPortalProjectActions.GetProject(presentation.ProjectId),
    ]);
  }

  const projectDetailsPresentationId = store.selectSnapshot(
    CustomerPortalProjectDetailsQueries.getPresentationId
  );
  // Check if we have project details loaded, if not, load it (fire-and-forget)
  if (projectDetailsPresentationId !== presentation.Id) {
    store.dispatch([
      new CustomerPortalProjectDetailsActions.GetProjectDetails(
        presentation.ProjectId
      ),
    ]);
  }
};
